import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Jobs } from "@/lib/api";
import { Briefcase, ArrowLeft, ArrowUpRight, ArrowDownRight } from "lucide-react";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" });

export const JobsList = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    Jobs.list().then(setData);
  }, []);
  if (!data) return <div className="text-sm text-neutral-500">Loading…</div>;
  const jobs = data.jobs || [];

  return (
    <div data-testid="jobs-page" className="fade-rise">
      <div className="swiss-label mb-3 inline-flex items-center gap-2">
        <Briefcase className="h-3 w-3" /> Jobs
      </div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight">
        Job Profitability
      </h1>
      <p className="text-neutral-600 mt-3 max-w-2xl">
        Tag any bill, receipt or income with a job code (e.g. <code>#smith-12-hill</code>)
        and Billy aggregates the profit. Tap a job for the full breakdown.
      </p>

      {jobs.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-neutral-300 p-10 text-center">
          <div className="font-display font-extrabold text-xl">No jobs yet</div>
          <p className="text-sm text-neutral-500 mt-2">
            Add a tag like <code>smith-12-hill</code> on a bill, receipt or income
            and it'll show up here.
          </p>
        </div>
      ) : (
        <div className="mt-8 border-2 border-black bg-white">
          <table className="w-full text-sm" data-testid="jobs-table">
            <thead>
              <tr className="bg-black text-white">
                <th className="text-left p-3 text-[10px] uppercase tracking-widest">Job tag</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-widest">Income exp.</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-widest">Cost</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-widest">Profit</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-widest">Margin</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr
                  key={j.tag}
                  className="border-t border-neutral-200 hover:bg-neutral-50"
                  data-testid={`job-row-${j.tag}`}
                >
                  <td className="p-3">
                    <Link to={`/jobs/${encodeURIComponent(j.tag)}`} className="font-bold hover:underline">
                      #{j.tag}
                    </Link>
                    <div className="text-[11px] text-neutral-500 mt-0.5">
                      {j.counts.bills} bills · {j.counts.receipts} receipts · {j.counts.incomes} incomes
                    </div>
                  </td>
                  <td className="p-3 mono text-right">{fmt(j.income_expected)}</td>
                  <td className="p-3 mono text-right text-amber-700">{fmt(j.cost_total)}</td>
                  <td
                    className={`p-3 mono text-right font-bold ${
                      j.profit_expected >= 0 ? "text-green-700" : "text-red-600"
                    }`}
                  >
                    {fmt(j.profit_expected)}
                  </td>
                  <td className="p-3 mono text-right">
                    {j.margin_expected_pct != null ? `${j.margin_expected_pct}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const JobDetail = () => {
  const { tag } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => {
    Jobs.get(tag).then(setData);
  }, [tag]);
  if (!data) return <div className="text-sm text-neutral-500">Loading…</div>;
  const s = data.summary;

  return (
    <div data-testid="job-detail" className="fade-rise">
      <Link
        to="/jobs"
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-black mb-6"
      >
        <ArrowLeft className="h-4 w-4" /> All jobs
      </Link>
      <div className="swiss-label mb-3 inline-flex items-center gap-2">
        <Briefcase className="h-3 w-3" /> Job
      </div>
      <h1 className="font-display font-black text-4xl tracking-tight">#{tag}</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        <Tile label="Income exp." value={fmt(s.income_expected)} />
        <Tile label="Income received" value={fmt(s.income_received)} accent="green" />
        <Tile label="Cost (bills + receipts)" value={fmt(s.cost_total)} accent="amber" />
        <Tile
          label="Profit (expected)"
          value={fmt(s.profit_expected)}
          accent={s.profit_expected >= 0 ? "green" : "red"}
        />
      </div>

      <Section title="Incomes" emptyText="No incomes tagged.">
        {data.incomes.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-neutral-300 text-[10px] uppercase tracking-widest">
                <th className="py-2">Date</th>
                <th className="py-2">Customer / Category</th>
                <th className="py-2 text-right">Amount</th>
                <th className="py-2 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.incomes.map((i) => (
                <tr key={i.id} className="border-b border-neutral-100">
                  <td className="py-2 mono">{i.expected_date}</td>
                  <td className="py-2">
                    <div className="font-bold">{i.client_name}</div>
                    <div className="text-[11px] text-neutral-500">{i.category}</div>
                  </td>
                  <td className="py-2 mono text-right">{fmt(i.amount)}</td>
                  <td className="py-2 text-right text-[11px] uppercase tracking-widest">
                    {i.received ? "Received" : "Expected"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Bills" emptyText="No bills tagged.">
        {data.bills.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-neutral-300 text-[10px] uppercase tracking-widest">
                <th className="py-2">Due</th>
                <th className="py-2">Company / Invoice</th>
                <th className="py-2 text-right">Amount</th>
                <th className="py-2 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.bills.map((b) => (
                <tr key={b.id} className="border-b border-neutral-100">
                  <td className="py-2 mono">{b.due_date}</td>
                  <td className="py-2">
                    <Link to={`/bills/${b.id}`} className="font-bold hover:underline">{b.company}</Link>
                    <div className="text-[11px] text-neutral-500">{b.invoice_number}</div>
                  </td>
                  <td className="py-2 mono text-right text-amber-700">{fmt(b.amount_due)}</td>
                  <td className="py-2 text-right text-[11px] uppercase tracking-widest">{b.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Receipts" emptyText="No receipts tagged.">
        {data.receipts.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-neutral-300 text-[10px] uppercase tracking-widest">
                <th className="py-2">Date</th>
                <th className="py-2">Vendor / Category</th>
                <th className="py-2 text-right">Total</th>
                <th className="py-2 text-right">GST</th>
              </tr>
            </thead>
            <tbody>
              {data.receipts.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100">
                  <td className="py-2 mono">{r.receipt_date}</td>
                  <td className="py-2">
                    <Link to={`/receipts/${r.id}`} className="font-bold hover:underline">{r.vendor}</Link>
                    <div className="text-[11px] text-neutral-500">{r.category}</div>
                  </td>
                  <td className="py-2 mono text-right text-amber-700">{fmt(r.total_amount)}</td>
                  <td className="py-2 mono text-right">{fmt(r.gst_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
};

const Tile = ({ label, value, accent }) => {
  const vColors = {
    green: "text-green-700",
    red: "text-red-600",
    amber: "text-amber-700",
  };
  return (
    <div className="border-2 border-black bg-white p-5">
      <div className="swiss-label">{label}</div>
      <div className={`font-display font-black text-2xl mono mt-1 ${vColors[accent] || ""}`}>{value}</div>
    </div>
  );
};

const Section = ({ title, emptyText, children }) => (
  <div className="mt-10">
    <h2 className="font-display font-extrabold text-xl uppercase tracking-widest border-b-2 border-black pb-2 mb-4">
      {title}
    </h2>
    {children || (
      <div className="text-sm text-neutral-500 italic">{emptyText}</div>
    )}
  </div>
);
